import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTimepicker, MatTimepickerInput, MatTimepickerToggle } from '@angular/material/timepicker';

import { CreateAppointmentService } from '@hm/core/services/create-appointment.service';
import { PartnersService } from '@hm/core/services/partners.service';
import { refreshPrescriptionsData } from '@hm/core/stores/prescriptions/prescriptions.actions';

import { SuggestionsListComponent } from '@hm/ui/lists/suggestions-list/suggestions-list.component';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';
import { formatDate } from '@lib/commons/util-functions/format-date.function';

import {
  AppointmentEventPlaceType,
  AppointmentSource,
  AppointmentSuggestionResponse,
  CreateAppointmentControls,
  CreateAppointmentModalType,
  PlanPrescriptionPayload,
  PrescriptionDetails,
} from '@lib/core/models/create-appointment.interface';
import { DoctorV2, LocationV2, PartnerSearchModelV2, PartnerV2 } from '@lib/core/models/partners.interface';
import { AppointmentTypes } from '@lib/core/models/visit.interface';

import { WarningMessageComponent } from '@lib/shared-ui/commons/warning-message/warning-message.component';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

export interface CreateAppointmentModalData {
  id?: string;
  type: CreateAppointmentModalType;
  userId?: string;
  appointmentType: AppointmentTypes;
  appointmentName: string;
  eventPlace?: AppointmentEventPlaceType;
  prescriptions: PrescriptionDetails[];
  startDate?: Date;
  partnerId?: string;
  partnerName?: string;
  address?: string;
  locationId?: string;
  doctorId?: string;
  price?: number;
}

@Component({
  selector: 'hm-create-appointment-modal',
  imports: [
    MatDialogContent,
    LoaderComponent,
    ReactiveFormsModule,
    MatFormField,
    MatError,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatInput,
    TranslatePipe,
    MatDatepicker,
    MatDatepickerToggle,
    MatDatepickerInput,
    MatDialogActions,
    MatDialogClose,
    MatTimepickerInput,
    MatTimepicker,
    MatTimepickerToggle,
    MatSuffix,
    MatAutocomplete,
    MatAutocompleteTrigger,
    WarningMessageComponent,
    SuggestionsListComponent,
  ],
  templateUrl: './create-appointment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAppointmentModalComponent implements OnInit {
  protected readonly data: CreateAppointmentModalData = inject(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly partnersService = inject(PartnersService);
  private readonly createAppointmentService = inject(CreateAppointmentService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogRef = inject(MatDialogRef<CreateAppointmentModalComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);

  protected readonly modalType = CreateAppointmentModalType;

  protected createAppointmentForm!: FormGroup<CreateAppointmentControls>;

  protected readonly loading = signal<boolean>(false);
  protected readonly partnerOptions = signal<PartnerV2[]>([]);
  protected readonly locationOptions = signal<LocationV2[]>([]);
  protected readonly doctorOptions = signal<DoctorV2[]>([]);
  protected readonly selectedDoctor = signal<DoctorV2 | null>(null);
  protected readonly selectedSuggestion = signal<AppointmentSuggestionResponse | null>(null);

  protected readonly type = computed<CreateAppointmentModalType>(() => this.data.type);
  protected readonly partnerName = computed<string>(() => this.data.partnerName);
  protected readonly address = computed<string>(() => this.data.address);
  protected readonly startDate = computed<string>(() => formatDate(this.data.startDate, true));

  protected get fc() {
    return this.createAppointmentForm.controls as CreateAppointmentControls;
  }

  ngOnInit() {
    this.initModal();
    this.setupDoctorSearch();
  }

  private initModal() {
    this.initForm();

    if (this.data.partnerId) this.getPartnerById(this.data.partnerId);
    else this.getPartners();
  }

  private initForm() {
    this.createAppointmentForm = this.fb.group<CreateAppointmentControls>({
      appointmentType: this.fb.control(this.data.appointmentType, [Validators.required]),
      appointmentName: this.fb.control(this.data.appointmentName, [Validators.required]),
      prescriptionIds: this.fb.control(this.data.prescriptions?.map(p => p.id) || [], [Validators.required]),
      partnerId: this.fb.control(null, [Validators.required]),
      locationId: this.fb.control(null, [Validators.required]),
      doctorId: this.fb.control(null),
      doctorName: this.fb.control(null, [Validators.required]),
      date: this.fb.control(null, [Validators.required]),
      startTime: this.fb.control(null, [Validators.required]),
      endTime: this.fb.control(null, [Validators.required]),
      eventPlace: this.fb.control(this.data.eventPlace, [Validators.required]),
      price: this.fb.control(null, [Validators.required]),
    });
  }

  private getPartners() {
    const partnerSearchPayload: PartnerSearchModelV2 = {
      pageSize: DEFAULT_PAGE_SIZE,
      active: true,
    };
    this.loading.set(true);

    return this.partnersService.searchPartnersV2(partnerSearchPayload).subscribe({
      next: result => this.partnerOptions.set(result.items),
      error: () => this.loading.set(false),
      complete: () => this.loading.set(false),
    });
  }

  private getPartnerById(partnerId: string) {
    this.loading.set(true);

    return this.partnersService
      .getPartnerByIdV2(partnerId)
      .pipe(
        tap(result => {
          this.partnerOptions.set([result]);
          this.fc.partnerId.setValue(result.id);
        }),
        switchMap(() => {
          return this.data.locationId
            ? this.partnersService.getPartnerLocationByIdV2(this.data.partnerId, this.data.locationId).pipe(
                tap(result => {
                  this.locationOptions.set([result]);
                  this.fc.locationId.setValue(result.id);
                })
              )
            : this.partnersService.searchPartnerLocationsV2(partnerId, { partnerId: partnerId }).pipe(tap(result => this.locationOptions.set(result.items)));
        })
      )
      .subscribe({
        next: () => this.loading.set(false),
        error: () => this.loading.set(false),
      });
  }

  private getPartnerLocations(partnerId: string) {
    return this.partnersService.searchPartnerLocationsV2(partnerId, { partnerId: partnerId }).subscribe({
      next: result => this.locationOptions.set(result.items),
    });
  }

  private setupDoctorSearch() {
    this.fc.doctorName.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(searched => searched.trim().length < 3),
        switchMap(searchTerm => {
          if (this.selectedDoctor() && this.selectedDoctor()!.fullName !== searchTerm) {
            this.selectedDoctor.set(null);
            this.fc.doctorId.setValue(null);
          }

          return this.partnersService.searchPartnerDoctorsV2(this.fc.partnerId.value!, {
            searchTerm: searchTerm,
            partnerId: this.fc.partnerId.value!,
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => this.doctorOptions.set(result.items),
        error: () => this.doctorOptions.set([]),
      });
  }

  private createNonExistingDoctor(partnerId: string, doctorName: string) {
    return this.partnersService.createPartnerDoctorV2(partnerId, {
      partnerId: partnerId,
      fullName: doctorName,
    });
  }

  protected selectPartner($event: PartnerV2) {
    this.fc.partnerId.setValue($event.id);

    if (this.type() !== CreateAppointmentModalType.BindToExisting && !this.data.locationId) {
      this.getPartnerLocations($event.id);
    }
  }

  protected selectLocation($event: LocationV2) {
    this.fc.locationId.setValue($event.id);
  }

  protected selectDoctor(doctor: DoctorV2) {
    this.selectedDoctor.set(doctor);
    this.fc.doctorId.setValue(doctor.id);
    this.fc.doctorName.setValue(doctor.fullName, { emitEvent: false });
    this.doctorOptions.set([]);
  }

  protected selectSuggestion(suggestion: AppointmentSuggestionResponse) {
    this.selectedSuggestion.set(suggestion);
  }

  private combineDateTime(date: Date, time: Date): string {
    date.setHours(time.getHours(), time.getMinutes(), 0, 0);

    return date.toISOString();
  }

  protected submit() {
    switch (this.type()) {
      case CreateAppointmentModalType.ManualCreation:
        return this.createAppointment();
      case CreateAppointmentModalType.BindToExisting:
        return this.bindAppointment();
      case CreateAppointmentModalType.Cancel:
        return this.cancelAppointment();
    }
  }

  private createAppointment() {
    this.loading.set(true);

    if (this.selectedDoctor()) this.proceedWithAppointmentCreation(this.selectedDoctor()!.id);
    else {
      const doctorName = this.fc.doctorName.value?.trim();

      this.createNonExistingDoctor(this.fc.partnerId.value!, doctorName).subscribe({
        next: newDoctor => {
          this.snackBar.open(`Лікаря "${doctorName}" створено успішно`, 'OK', { duration: 3000 });
          this.proceedWithAppointmentCreation(newDoctor.id);
        },
        error: () => {
          this.snackBar.open('Не вдалось створити лікаря', 'OK', { duration: 5000 });
          this.loading.set(false);
        },
      });
    }
  }

  private proceedWithAppointmentCreation(doctorId: string) {
    const payload: PlanPrescriptionPayload = {
      userId: this.data.userId,
      partnerId: this.fc.partnerId.value!,
      locationId: this.fc.locationId.value!,
      prescriptions: this.data.prescriptions || [],
      doctorId: doctorId,
      startTime: this.combineDateTime(this.fc.date.value!, this.fc.startTime.value!),
      endTime: this.combineDateTime(this.fc.date.value!, this.fc.endTime.value!),
      source: AppointmentSource.Manual,
      price: this.fc.price.value!,
      eventPlace: AppointmentEventPlaceType.Hospital,
    };

    this.createAppointmentService.createAppointment(payload).subscribe({
      next: () => {
        this.store.dispatch(refreshPrescriptionsData());
        this.snackBar.open('Візит успішно створено', 'OK', { duration: 5000 });
        this.dialogRef.close(payload);
      },
      error: () => {
        this.snackBar.open('Не вдалось створити візит', 'OK', { duration: 5000 });
        this.loading.set(false);
      },
      complete: () => this.loading.set(false),
    });
  }

  private bindAppointment() {
    return;
  }

  private cancelAppointment() {
    this.loading.set(true);

    this.createAppointmentService.cancelAppointment(this.data.prescriptions[0].id, this.data.id).subscribe({
      next: () => {
        this.store.dispatch(refreshPrescriptionsData());
        this.snackBar.open('Візит успішно видалено', 'OK', { duration: 5000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackBar.open('Не вдалось видалити візит', 'OK', { duration: 5000 });
        this.loading.set(false);
      },
    });
  }
}
